import React from 'react';
import {Container, Row, Col} from 'react-bootstrap';
import {motion} from 'framer-motion';
import Seo from '../../../shared/seo/Seo';
import {
    FaIndustry,
    FaUsers,
    FaProjectDiagram,
    FaGlobeAfrica,
    FaShieldAlt,
    FaUsersCog,
    FaHandshake,
    FaWater,
    FaBolt,
    FaTools,
    FaSnowflake,
    FaFire,
    FaBuilding,
    FaResolving
} from 'react-icons/fa';

// Components
import PageHeader from 'features/common/PageHeader';
import ContactCTA from 'features/common/ContactCTA';
import { getCanonicalUrl, pageMeta } from 'shared/seo/seoMeta';


// Types
import {Stat, CompanyValue} from '../types/aboutTypes';

// Styles
import './AboutPage.css';
import {useTranslation} from "react-i18next";

// Expertise areas with React icons for domaines d'expertise
export const expertiseAreas = [
    {icon: <FaWater/>}, // Traitement des eaux & assainissement
    {icon: <FaBolt/>}, // Électricité & automatisation
    {icon: <FaTools/>}, // Maintenance & installation industrielle
    {icon: <FaSnowflake/>}, // Froid & climatisation
    {icon: <FaFire/>}, // Sécurité incendie
    {icon: <FaBuilding/>}  // Bâtiment & travaux divers
];


const AboutPage: React.FC = () => {
    const {t} = useTranslation();

    const fadeIn = {
        hidden: {opacity: 0, y: 20},
        visible: {opacity: 1, y: 0, transition: {duration: 0.6}}
    };

    const staggerContainer = {
        hidden: {opacity: 0},
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

// Use translations for company values
    const translatedValues = [
        {
            id: 'responsability',
            icon: <FaResolving/>,
            title: t('about.values.responsability.title', 'Responsabilité'),
            description: t(
                'about.values.responsability.description',
                'Nous assumons pleinement nos engagements envers nos clients, nos équipes et l’environnement. Chaque mission est menée avec sérieux, éthique et rigueur professionnelle.'
            )
        },
        {
            id: 'reactivity',
            icon: <FaBolt/>,
            title: t('about.values.reactivity.title', 'Réactivité'),
            description: t(
                'about.values.reactivity.description',
                'Parce que chaque minute compte, nous garantissons des interventions rapides et efficaces, avec une organisation souple et orientée résultats.'
            )
        },
        {
            id: 'quality_safety',
            icon: <FaShieldAlt/>,
            title: t('about.values.quality_safety.title', 'Qualité & sécurité'),
            description: t(
                'about.values.quality_safety.description',
                'Nous veillons à la fiabilité des équipements, au respect des normes et à la sécurité des personnes et des installations, à chaque étape du projet.'
            )
        },
        {
            id: 'solidarity',
            icon: <FaUsersCog/>,
            title: t('about.values.solidarity.title', 'Solidarité'),
            description: t(
                'about.values.solidarity.description',
                'Nous plaçons l’humain au centre de nos actions. En interne comme avec nos clients et partenaires, nous favorisons la coopération, le soutien mutuel et la réussite collective.'
            )
        },
        {
            id: 'customer_proximity',
            icon: <FaHandshake/>,
            title: t('about.values.customer_proximity.title', 'Proximité client'),
            description: t(
                'about.values.customer_proximity.description',
                'Nous construisons une relation de confiance à long terme, fondée sur l’écoute, le conseil personnalisé et un accompagnement sur mesure.'
            )
        }
    ];


    const strategicAxes = t('about.axes', {returnObjects: true}) as { title: string; details: string[] }[];
    const translatedAxes = strategicAxes.map((axis, index) => ({
        title: axis.title,
        items: axis.details
    }));


// Stats data
    var companyStats: Stat[] = [
        {
            icon: <FaIndustry/>,
            number: `${new Date().getFullYear() - 2021}+`,
            title: t('about.stats.experienceYears', 'Années d\'Expérience')
        },
        {
            icon: <FaUsers/>,
            number: '35+',
            title: t('about.stats.clients', 'Clients Satisfaits')
        },
        {
            icon: <FaProjectDiagram/>,
            number: '25+',
            title: t('about.stats.projects', 'Projets Réalisés')
        },
        {
            icon: <FaGlobeAfrica/>,
            number: '+95%',
            title: t('about.stats.satisfaction', 'Taux de satisfaction')
        }
    ];


    // Translated timeline events
    const timelineEvents = t('about.timeline', {returnObjects: true}) as {
        year: string;
        title: string;
        description: string
    }[];


    return (
        <>
            <div className="page-wrapper">
                <Seo
                  title={pageMeta.about.title}
                  description={pageMeta.about.description}
                  canonical={getCanonicalUrl(pageMeta.about.canonicalPath)}
                />

                <PageHeader
                    title={t('about.title', 'Présentation de l’entreprise')}
                    subtitle=""
                    background='/images/background/about-bg.webp'
                />

                {/* Intro Section */}
                <section className="about-intro">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg={10}>
                                <motion.div
                                    initial="hidden"
                                    animate="visible"
                                    variants={fadeIn}
                                    className="text-center mb-5"
                                >
                                    <h2 className="display-5 fw-bold mb-4" style={{ fontFamily: 'Stencil' }}>
                                        <span style={{ color: 'var(--secondary)' }}>A</span>
                                        <span style={{ color: 'var(--primary)' }}>FRICA </span>
                                        <span style={{ color: 'var(--secondary)' }}>S</span>
                                        <span style={{ color: 'var(--primary)' }}>UPPLY </span>
                                        <span style={{ color: 'var(--secondary)' }}>S</span>
                                        <span style={{ color: 'var(--primary)' }}>ERVICE Sarl</span>
                                    </h2>
                                    <p className="lead mb-4">{t('about.introduction', 'Africa Supply Service Sarl est une entreprise marocaine fondée en 2021, spécialisée dans la fourniture de solutions techniques et industrielles sur mesure. Notre force réside dans une équipe hautement qualifiée, cumulant plus de 23 ans d’expérience dans les secteurs de l’industrie, de l’eau, de l’énergie et de la maintenance technique. Grâce à cette expertise, nous répondons avec professionnalisme, réactivité et efficacité aux besoins les plus complexes de nos clients.')}</p>
                                </motion.div>
                            </Col>
                        </Row>
                        <Row className="mt-5">
                            <Col md={6} className="mb-4">
                                <motion.div
                                    className="mission-box"
                                    initial="hidden"
                                    animate="visible"
                                    variants={fadeIn}
                                    transition={{delay: 0.2}}
                                >
                                    <h3>{t('about.mission', 'Notre Mission')}</h3>
                                    <p>
                                        {t('about.mission-desc', 'Fournir des solutions industrielles innovantes, durables et parfaitement adaptées aux besoins de nos clients, tout en assurant les plus hauts standards de qualité, de sécurité et de respect de l’environnement.')}
                                    </p>
                                </motion.div>
                            </Col>
                            <Col md={6} className="mb-4">
                                <motion.div
                                    className="vision-box"
                                    initial="hidden"
                                    animate="visible"
                                    variants={fadeIn}
                                    transition={{delay: 0.3}}
                                >
                                    <h3>{t('about.vision', 'Notre Vision')}</h3>
                                    <p>
                                        {t('about.vision-desc', 'Devenir un acteur de référence au Maroc et en Afrique dans le domaine des solutions techniques et industrielles, reconnu pour notre expertise, notre engagement et notre capacité à innover.')}
                                    </p>
                                </motion.div>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Expertise Section */}
                <section className="about-expertise">
                    <Container>
                        <Row className="justify-content-center mb-5">
                            <Col lg={10} className="text-center">
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{once: true}}
                                    variants={fadeIn}
                                >
                                    <h2 className="section-title">{t('about.expertiseTitle', 'Domaines d\'expertise')}</h2>
                                    <p className="lead mb-5">{t('about.expertise-desc', 'Nous intervenons à travers cinq axes stratégiques, chacun conçu pour répondre à des besoins spécifiques :')}
                                    </p>
                                </motion.div>
                            </Col>
                        </Row>

                        <Row className="expertise-row">
                            {translatedAxes.map((area: { title: string; items: string[] }, index: number) => (
                                <Col md={6} lg={4} className="mb-4" key={index}>
                                    <motion.div
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{once: true}}
                                        variants={fadeIn}
                                        transition={{delay: index * 0.1}}
                                        className="expertise-item"
                                    >
                                        <div className="expertise-item-inner">
                                            <div className="expertise-icon">
                                                {expertiseAreas[index]?.icon || <FaTools/>}
                                            </div>
                                            <h3>{area.title}</h3>
                                            {area.items.map((item: string, i: number) => (
                                                <p key={i}>{item}</p>
                                            ))}
                                        </div>
                                    </motion.div>
                                </Col>
                            ))}
                        </Row>

                    </Container>
                </section>

                {/* History Timeline Section */}
                <section className="about-commitment">
                    <Container>
                        <Row className="justify-content-center mb-5">
                            <Col lg={10} className="text-center">
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{once: true}}
                                    variants={fadeIn}
                                >
                                    <h2 className="section-title">{t('about.engagement', 'Notre Engagement')}</h2>
                                    <p className="lead mb-5">{t('about.engagement-desc', 'Grâce à notre savoir-faire, notre réseau de partenaires et notre engagement envers la qualité, la durabilité et la sécurité, nous accompagnons nos clients dans la réussite de leurs projets industriels et techniques.')}</p>
                                </motion.div>
                            </Col>
                        </Row>


                        <div className="timeline" style={{direction: 'ltr'}}>
                            {timelineEvents.map((event: {
                                year: string;
                                title: string;
                                description: string
                            }, index: number) => (
                                <motion.div
                                    key={index}
                                    className={`timeline-item ${index % 2 === 0 ? 'timeline-left' : 'timeline-right'}`}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{once: true}}
                                    variants={fadeIn}
                                    transition={{delay: index * 0.1}}
                                >
                                    <div className="timeline-content">
                                        <div className="timeline-year">{event.year}</div>
                                        <h4>{event.title}</h4>
                                        <p>{event.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>


                    </Container>
                </section>

                {/* Values Section */}
                <section className="about-expertise">
                    <Container>
                        <Row className="justify-content-center mb-5">
                            <Col lg={10} className="text-center">
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{once: true}}
                                    variants={fadeIn}
                                >
                                    <h2 className="section-title">{t('about.valuesTitle', 'Nos Valeurs Fondamentales')}</h2>
                                    <p className="lead mb-5">{t('about.valuesTitle', 'Ces principes animent chacune de nos décisions et incarnent l’essence même de notre entreprise. Ils sont au cœur de notre vision, de nos partenariats et de nos projets.')}
                                    </p>
                                </motion.div>
                            </Col>
                        </Row>

                        <motion.div
                            className="values-grid"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{once: true}}
                            variants={staggerContainer}
                        >
                            {translatedValues.map((value: CompanyValue, index: number) => (
                                <motion.div
                                    key={value.id || index}
                                    className="value-item"
                                    variants={fadeIn}
                                    transition={{delay: index * 0.1}}
                                >
                                    <div className="value-icon">
                                        {value.icon}
                                    </div>
                                    <h3 className="value-title">{value.title}</h3>
                                    <p>{value.description}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </Container>
                </section>

                {/* Stats Section */}
                <section className="stats-section">
                    <Container>
                        <Row className="justify-content-center mb-5">
                            <Col lg={10} className="text-center">
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{once: true}}
                                    variants={fadeIn}
                                >
                                    <h2 className="section-title text-white">{t('about.stats.title', 'Nos Réalisations')}</h2>
                                    <p className="stats-subtitle mb-5">{t('about.stats.subtitle', 'Quelques chiffres clés sur notre impact')}</p>
                                </motion.div>
                            </Col>
                        </Row>

                        <motion.div
                            className="stats-grid"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{once: true}}
                            variants={staggerContainer}
                        >
                            {companyStats.map((stat: Stat, index: number) => (
                                <motion.div
                                    key={index}
                                    className="stat-item"
                                    variants={fadeIn}
                                    transition={{delay: index * 0.1}}
                                >
                                    <div className="stat-icon">{stat.icon}</div>
                                    <div className="stat-number">{stat.number}</div>
                                    <div className="stat-title">{stat.title}</div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </Container>
                </section>


                {/* CTA Section */}
                <ContactCTA/>
            </div>
        </>
    );
};

export default AboutPage;
